import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, BadgeCheck, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => (n === null || n === undefined || n === "" ? "—" : `₹ ${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const dt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");
const Row = ({ k, v, testId }) => (
  <div className="flex justify-between gap-3 text-sm"><span className="text-slate-500 shrink-0">{k}</span><span className="font-medium text-right break-words" data-testid={testId}>{v || "—"}</span></div>
);
const Box = ({ title, children, testId }) => (
  <div className="rounded-md border p-3 space-y-1" data-testid={testId}>
    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{title}</div>
    {children}
  </div>
);

function SummaryBox({ c }) {
  const d = c.details || {};
  return (
    <Box title="Request" testId="chain-dlg-summary">
      {(c.details?.inv_ref || (c.details?.settlement_inv_refs || []).length > 0) && <Row k="RKF ref" v={c.details.inv_ref || c.details.settlement_inv_refs.join(", ")} testId="rd-inv-ref" />}
      {c.details?.status_label && <Row k="Status" v={c.details.status_label} testId="rd-status-label" />}
      <Row k="Requester" v={c.requester_name} />
      <Row k="Project" v={c.project_name} />
      {d.expense_month && <Row k="Expense Month" v={d.expense_month} />}
      <Row k="Filed on" v={dt(d.filed_at)} />
      <Row k="Budget line" v={c.budget_line?.label} />
      <Row k="Purpose" v={d.purpose} testId="rd-purpose" />
      {d.vendor_name && <Row k="Vendor / shop" v={d.vendor_name} testId="rd-vendor" />}
      {d.vendor_pan && <Row k="Vendor PAN" v={d.vendor_pan} testId="rd-vendor-pan" />}
      {d.vendor_gstin && <Row k="Vendor GSTIN" v={d.vendor_gstin} testId="rd-vendor-gstin" />}
      {d.place_of_supply && <Row k="Place of supply" v={d.place_of_supply} />}
      {c.kind !== "advance" && <Row k="Vendor bill no. / date" v={`${d.no_vendor_bill_no ? "— (none)" : (d.invoice_no || "—")} · ${d.invoice_date ? String(d.invoice_date).slice(0, 10) : "—"}`} testId="rd-invoice" />}
      {c.kind === "advance" && <Row k="Expected settlement" v={d.expected_settlement_date ? String(d.expected_settlement_date).slice(0, 10) : "—"} />}
      {c.kind === "advance" && <Row k="Planned use" v={d.planned_use || "Not entered on the form"} />}
      {d.notes && <Row k="Requester notes" v={d.notes} testId="rd-notes" />}
      <Row k="Amount" v={inr(c.amount)} testId="chain-dlg-amount" />
    </Box>
  );
}

function Particulars({ c }) {
  const d = c.details || {};
  if (c.kind === "advance") {
    const prev = d.unsettled_advances || [];
    return (
      <Box title="Previous unsettled advances" testId="rd-unsettled">
        {prev.length ? prev.map(p => <Row key={p.ref} k={`${p.ref} · ${p.age_days ?? "?"} days`} v={`${inr(p.outstanding)} of ${inr(p.amount)}`} />)
          : <p className="text-xs text-slate-500">None</p>}
      </Box>
    );
  }
  const lines = d.lines || [];
  return (
    <Box title="Particulars" testId="rd-particulars">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-slate-500 text-left"><th className="py-1">Description</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th><th className="text-right">GST %</th></tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-t" data-testid={`rd-line-${i}`}>
                <td className="py-1 pr-2">{l.description}</td><td className="text-right">{l.qty || "—"}</td><td className="text-right">{l.rate ? inr(l.rate) : "—"}</td>
                <td className="text-right font-mono">{inr(l.amount)}</td><td className="text-right">{l.gst_rate ?? "—"}</td>
              </tr>
            ))}
            {!lines.length && <tr><td colSpan={5} className="py-1 text-slate-500">No line items entered.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="border-t pt-1 mt-1 space-y-0.5">
        <Row k="Sub-total" v={inr(d.sub_total)} /><Row k="GST" v={inr(d.total_gst)} />
        {!!d.round_off && <Row k="Round-off" v={inr(d.round_off)} />}
        <Row k="Invoice total" v={inr(d.invoice_total)} testId="rd-invoice-total" />
        <Row k="TDS" v={d.tds_confirmed ? `${d.tds_section || "No TDS"} · ${d.tds_rate ?? 0}% · ${inr(d.tds_amount)}` : "Not set yet"} testId="rd-tds" />
        <Row k="Net payable" v={inr(d.net_payable)} testId="rd-net" />
      </div>
    </Box>
  );
}

function Thumb({ f, onOpen }) {
  const [url, setUrl] = useState(null);
  const isImg = (f.content_type || "").startsWith("image/");
  useEffect(() => {
    let u;
    if (isImg) { api.get(`/files/${f.id}/download`, { responseType: "blob" }).then(r => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {}); }
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [f.id, isImg]);
  return (
    <button type="button" onClick={() => onOpen(f)} className="w-28 text-left rounded border bg-white hover:border-slate-400 p-1" data-testid={`rd-thumb-${f.id}`}>
      <div className="h-20 flex items-center justify-center bg-slate-50 rounded overflow-hidden">
        {isImg && url ? <img src={url} alt={f.name} className="object-cover h-full w-full" /> : <FileText className="w-8 h-8 text-slate-400" />}
      </div>
      <div className="text-[10px] truncate mt-1">{f.name}</div>
      {f.replaced && <div className="text-[10px] font-semibold text-amber-700" data-testid={`rd-replaced-${f.id}`}>Replaced</div>}
      <div className="text-[10px] text-slate-400">{dt(f.uploaded_at)}</div>
    </button>
  );
}

function Attachments({ c }) {
  const [prev, setPrev] = useState(null);
  const files = c.file_meta || [];
  const open = async (f) => {
    setPrev({ f, loading: true });
    try {
      const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: f.content_type || r.data.type });
      setPrev({ f, url: URL.createObjectURL(blob) });
    } catch (e) { setPrev({ f, error: e.response?.status === 403 ? "You don't have access to this file" : "Unable to open file" }); }
  };
  const close = () => { if (prev?.url) URL.revokeObjectURL(prev.url); setPrev(null); };
  return (
    <Box title={`Attachments (${files.length})`} testId="chain-dlg-files">
      {files.length ? <div className="flex flex-wrap gap-2">{files.map(f => <Thumb key={f.id} f={f} onOpen={open} />)}</div>
        : <p className="text-xs text-slate-500">No documents attached.</p>}
      {prev && (
        <div className="mt-2 border rounded" data-testid="rd-preview">
          <div className="flex justify-between items-center px-2 py-1 bg-slate-50 text-xs"><span className="truncate">{prev.f.name}</span>
            <button type="button" onClick={close} data-testid="rd-preview-close"><X className="w-3.5 h-3.5" /></button></div>
          {prev.loading && <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
          {prev.error && <p className="p-3 text-xs text-rose-600">{prev.error}</p>}
          {prev.url && ((prev.f.content_type || "").startsWith("image/")
            ? <img src={prev.url} alt={prev.f.name} className="max-h-[60vh] mx-auto" />
            : <iframe src={prev.url} title={prev.f.name} className="w-full h-[60vh]" />)}
        </div>
      )}
    </Box>
  );
}

function VendorVerify({ c }) {
  const p = c.payee || {};
  const [status, setStatus] = useState(p.status);
  const [busy, setBusy] = useState(false);
  if (c.kind !== "expense" || !p.payee_id) return null;
  if (status === "verified") return <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="rd-vendor-verified"><BadgeCheck className="w-3.5 h-3.5" /> Vendor verified</p>;
  if (status !== "pending") return null;
  const verify = async () => {
    if (!window.confirm(`Verify "${p.name}" in the Payees master? Check PAN, GSTIN and bank details first.`)) return;
    setBusy(true);
    try { await api.post(`/v2/payees/${p.payee_id}/verify`); setStatus("verified"); toast.success(`${p.name} verified`); }
    catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 mt-1" data-testid="rd-vendor-pending">
      <span className="text-xs text-amber-800">Vendor pending verification</span>
      {c.unmasked && <button type="button" disabled={busy} onClick={verify} className="text-xs font-medium rounded border border-amber-400 bg-white px-2 py-0.5 hover:bg-amber-100 disabled:opacity-50" data-testid="rd-vendor-verify-btn">{busy ? "Verifying…" : "Verify vendor"}</button>}
    </div>
  );
}

function BudgetBank({ c }) {
  const b = c.budget_line;
  const p = c.payee || {};
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Box title="Budget line" testId="chain-dlg-budget">
        {b ? <><div className="text-sm font-medium">{b.label}</div><Row k="Working" v={inr(b.working)} /><Row k="Spent" v={inr(b.spent)} /><Row k="Pending" v={inr(b.pending)} /><Row k="Remaining" v={inr(b.remaining)} testId="chain-dlg-remaining" /></>
          : <p className="text-xs text-slate-500">No budget line linked.</p>}
      </Box>
      <Box title="Payee bank" testId="chain-dlg-bank">
        <div className="text-sm font-medium">{p.name}</div>
        <Row k="Bank" v={p.bank_name} /><Row k="IFSC" v={p.ifsc} />
        <Row k="Account" v={p.account_full || p.account_masked} testId="chain-dlg-account" />
        {p.pan && <Row k="PAN" v={p.pan} testId="rd-payee-pan" />}
        {!p.bank_complete && <p className="text-xs text-amber-700">Bank details incomplete – payment waits until verified.</p>}
        <VendorVerify c={c} />
      </Box>
    </div>
  );
}

function History({ c }) {
  const h = c.history || [];
  return (
    <Box title="History" testId="rd-history">
      {h.length ? <ol className="space-y-1.5">{h.map((x, i) => (
        <li key={i} className="text-xs border-l-2 border-slate-200 pl-2" data-testid={`rd-history-${i}`}>
          <div><span className="font-medium">{x.action}</span> · {x.by_name}{x.by_role ? ` (${x.by_role})` : ""} · <span className="text-slate-500">{dt(x.at)}</span></div>
          {x.comment && <div className="text-slate-600">{x.comment}</div>}
          {(x.changes || []).map((ch, j) => <div key={j} className="text-slate-600">{ch.field}: <span className="line-through">{String(ch.old ?? "—")}</span> → <b>{String(ch.new ?? "—")}</b></div>)}
        </li>
      ))}</ol> : <p className="text-xs text-slate-500">No history yet.</p>}
    </Box>
  );
}

export function RequestDetails({ c }) {
  return (
    <div className="space-y-3" data-testid="request-details-panel">
      {c.details?.sent_back && <div className="rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800" data-testid="rd-sentback">Sent back by {c.details.sent_back.by}{c.details.sent_back.role ? `, ${c.details.sent_back.role}` : ""}: {c.details.sent_back.reason}</div>}
      <SummaryBox c={c} />
      {!!c.flags?.length && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 space-y-0.5" data-testid="rd-flags">
          {c.flags.map((f, i) => <div key={i} className="text-xs text-amber-800 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{f}</div>)}
        </div>
      )}
      <Particulars c={c} />
      <Attachments c={c} />
      <BudgetBank c={c} />
      <History c={c} />
      {!!c.details?.versions?.length && (
        <Box title="Versions" testId="rd-versions">
          {c.details.versions.map((v, i) => (
            <div key={i} className="text-xs border-l-2 border-blue-200 pl-2 mb-1.5" data-testid={`rd-version-${v.n}`}>
              <div><b>Version {v.n}</b> · {v.by_name} · {dt(v.at)} · {v.route === "original" ? "original submission" : v.route === "restart" ? "route restarted" : "returned to reviewer"}</div>
              {v.route === "original" && v.sent_back_reason && <div className="text-rose-700">Sent back: {v.sent_back_reason}</div>}
              <div className="text-slate-600">Response: {v.response}</div>
              {(v.changes || []).map((ch, j) => <div key={j} className="text-slate-600">{ch.field}: <span className="line-through">{String(ch.old || "—")}</span> → <b>{String(ch.new || "—")}</b></div>)}
            </div>
          ))}
        </Box>
      )}
    </div>
  );
}

export function RequestDetailsLoader({ kind, id }) {
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!id) { return; }
    api.get(`/chain/${kind}/${id}/context`).then(r => setC(r.data)).catch(e => setErr(e.response?.data?.detail || "Could not load details"));
  }, [kind, id]);
  if (err) return <p className="text-xs text-rose-600">{err}</p>;
  if (!c) return <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  return <RequestDetails c={c} />;
}
